import { validateEmail } from "~/utils/strings";

const NO_NAME = "(no name)";
const LEGACY_UNDEFINED_NAME = "undefined undefined";

export const UNKNOWN_USER_DISPLAY_NAME = "Unknown user";

export function formatUserName(user: { firstName?: string | null; lastName?: string | null }): string {
  const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return name || NO_NAME;
}

export function isUsableDisplayName(name: string | null | undefined): name is string {
  if (!name) return false;
  const trimmed = name.trim();
  return trimmed.length > 0 && trimmed !== NO_NAME && trimmed !== LEGACY_UNDEFINED_NAME;
}

export type UserDisplayNameFields = {
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  machineName?: string | null;
  userName?: string | null;
  email?: string | null;
};

export function resolveUserDisplayName(user: UserDisplayNameFields): string | null {
  const displayName = user.displayName?.trim();
  if (isUsableDisplayName(displayName) && !validateEmail(displayName)) return displayName;

  const formatted = formatUserName(user);
  if (isUsableDisplayName(formatted)) return formatted;

  const machineName = user.machineName?.trim();
  if (isUsableDisplayName(machineName)) return machineName;

  const userName = user.userName?.trim();
  if (isUsableDisplayName(userName)) return userName;

  const email = user.email?.trim();
  if (isUsableDisplayName(email)) return email;

  return null;
}

export function resolveUserDisplayNameOrUnknown(user: UserDisplayNameFields): string {
  return resolveUserDisplayName(user) ?? UNKNOWN_USER_DISPLAY_NAME;
}

/**
 * Resolves a personal name only. Unlike {@link resolveUserDisplayName}, an email address is never
 * an acceptable answer, so callers that show name and email separately never render one twice.
 */
export function resolveNonEmailDisplayName(
  user: UserDisplayNameFields | undefined,
  fallbackName?: string | null,
): string {
  if (user) {
    const fromNames = formatUserName(user);
    if (isUsableDisplayName(fromNames)) {
      return fromNames;
    }

    const resolved = resolveUserDisplayName(user);
    if (isUsableDisplayName(resolved) && !validateEmail(resolved)) {
      return resolved;
    }
  }

  const fallback = fallbackName?.trim();
  if (isUsableDisplayName(fallback) && !validateEmail(fallback)) {
    return fallback;
  }

  return UNKNOWN_USER_DISPLAY_NAME;
}
